import React from 'react';
import { getPosterUrl, formatReleaseDate } from '../services/api';
import { trackEvent } from '../services/analytics';
import { useRegion } from '../contexts/RegionContext';
import { pickProviderBlock } from '../services/regions';

/**
 * Editorial result card (design handoff). Poster-led with a 2:3 ratio + inset
 * hairline; below it the title, ★/year meta, an italic one-line "why it matched"
 * (drawn from the overview), the "Available on" streamer logos, and a quiet
 * Watch Trailer link. Dark "film magazine" palette. Click → detail modal.
 */
const MovieCard = ({ movie, onClick, showSimilarityScore = true, onPlayTrailer }) => {
  const posterUrl = getPosterUrl(movie.poster_path, 'w342');
  const releaseYear = formatReleaseDate(movie.release_date);
  const displayTitle = movie.title || movie.name;
  const isMovie = (movie.type || 'movie') === 'movie';
  const trailerKey = movie.trailer_key;
  const why = (movie.overview || '').split('. ')[0];

  const { region } = useRegion();
  const watchProviders = pickProviderBlock(movie.watch_providers, region);
  const streamingProviders = watchProviders?.flatrate || [];

  const handleCardClick = () => {
    trackEvent('Result Clicked', { movie_id: movie.id, title: displayTitle, score: movie.similarity_score });
    if (onClick) onClick(movie);
  };

  const handlePlayTrailer = (e) => {
    e.stopPropagation();
    trackEvent('Trailer Played', { movie_id: movie.id, title: displayTitle });
    if (onPlayTrailer) onPlayTrailer(trailerKey);
    else if (window.openTrailerModal) window.openTrailerModal(trailerKey);
    else window.open(`https://www.youtube.com/watch?v=${trailerKey}`, '_blank');
  };

  return (
    <div
      onClick={handleCardClick}
      className="group flex flex-col gap-2.5 cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
    >
      {/* Poster */}
      <div
        className="relative aspect-[2/3] rounded overflow-hidden"
        style={{ background: 'rgba(241,236,226,0.06)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}
      >
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={displayTitle}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div
          className="w-full h-full items-center justify-center text-center px-3 font-serif text-binge-soft text-sm"
          style={{ display: posterUrl ? 'none' : 'flex' }}
        >
          {displayTitle}
        </div>

        {/* Type tag */}
        <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[9px] font-semibold rounded tracking-[0.14em] uppercase bg-black/55 text-binge-ink backdrop-blur-sm">
          {isMovie ? 'Film' : 'Series'}
        </span>

        {/* Match score */}
        {showSimilarityScore && movie.similarity_score ? (
          <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-black/55 text-binge-gold backdrop-blur-sm">
            {Math.round(movie.similarity_score * 100)}%
          </span>
        ) : null}

        {/* Hover trailer button */}
        {trailerKey && (
          <button
            onClick={handlePlayTrailer}
            aria-label="Watch trailer"
            className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-binge-gold hover:text-binge-bg"
          >
            <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          </button>
        )}
      </div>

      {/* Title + rating */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-[13.5px] leading-tight text-binge-cream line-clamp-2">{displayTitle}</span>
        {movie.vote_average > 0 && (
          <span className="text-xs text-binge-sub whitespace-nowrap">★ {movie.vote_average.toFixed(1)}</span>
        )}
      </div>
      <p className="text-[11px] text-binge-faint -mt-1.5">
        {releaseYear}{!isMovie && movie.number_of_seasons ? ` · ${movie.number_of_seasons}S` : ''}
      </p>

      {/* Why it matched */}
      {why && (
        <p className="font-serif italic text-[13px] leading-snug text-binge-muted line-clamp-2 -mt-1">{why}</p>
      )}

      {/* Available on */}
      {streamingProviders.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {streamingProviders.slice(0, 4).map((p, i) => (
            <span key={i} title={p.provider_name} className="inline-flex items-center">
              {p.logo_path ? (
                <img src={`https://image.tmdb.org/t/p/w92${p.logo_path}`} alt={p.provider_name} className="h-5 w-5 rounded" />
              ) : (
                <span className="text-[10px] bg-white/10 text-binge-sub rounded px-1.5 py-0.5">{p.provider_name}</span>
              )}
            </span>
          ))}
          {streamingProviders.length > 4 && (
            <span className="text-[10px] text-binge-faint self-center">+{streamingProviders.length - 4}</span>
          )}
        </div>
      )}

      {/* Watch trailer */}
      {trailerKey && (
        <button
          onClick={handlePlayTrailer}
          className="self-start inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase text-binge-gold hover:text-binge-ink transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          Watch trailer
        </button>
      )}
    </div>
  );
};

export default MovieCard;
